import React from 'react';
import {
  Box,
  Card,
  CardActionArea,
  Typography,
  alpha,
  styled,
  type SxProps,
  type Theme,
} from '@mui/material';
import { radius } from 'themes/radius';
import { ReactComponent as TrackingProgramIcon } from 'assets/icons/TrackingProgram.svg';
import { ReactComponent as EducationalProgramIcon } from 'assets/icons/EducationalProgram.svg';
import { CareProgramStatusPill } from 'components/CareProgramStatusPill/CareProgramStatusPill';
import { CareProgramType } from '../../features/resources/types';

const StatusPillWrapper = styled(Box)(({ theme }) => ({
  marginLeft: 'auto',
  marginRight: theme.spacing(3.25),
}));

export type CareProgramCardProps = {
  title: string;
  programType?: CareProgramType;
  selected?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  icon?: React.ReactNode;
  statusLabel?: string;
  ariaLabel?: string;
  sx?: SxProps<Theme>;
  testId?: string;
};

const ICON_CONTAINER_SIZE = 38;
const ICON_SIZE = 24;
// Selected-state border width, applied on all sides.
const SELECTED_BORDER_WIDTH = 1.5;

// Default program icons use the imported SVGs, which fill with `currentColor`
// so they inherit the cobrand brand-secondary color set on the icon container
// below (consistent with the mobile icons).
const getDefaultIcon = (programType: CareProgramType): React.ReactNode =>
  programType === 'Educational Program' ? (
    <EducationalProgramIcon width={ICON_SIZE} height={ICON_SIZE} aria-hidden focusable={false} />
  ) : (
    <TrackingProgramIcon width={ICON_SIZE} height={ICON_SIZE} aria-hidden focusable={false} />
  );

export const CareProgramCard: React.FC<CareProgramCardProps> = ({
  title,
  programType = 'Health Tracker',
  selected = false,
  disabled = false,
  onClick,
  icon,
  statusLabel,
  ariaLabel,
  sx,
  testId = 'care-program-card',
}) => (
  <Card
    data-testid={testId}
    className="care-program-card"
    elevation={0}
    sx={[
      {
        width: '100%',
        alignSelf: 'stretch',
        borderRadius: radius.card,
        bgcolor: 'background.paper',
        boxShadow: (theme) => `0 1px 4px 0 ${theme.palette.grey[300]}`,
        // Selected: brand PRIMARY outline on ALL sides. A transparent border of
        // the same width is always reserved so selecting never shifts layout.
        border: (theme) =>
          selected
            ? `${SELECTED_BORDER_WIDTH}px solid ${theme.palette.primary.main}`
            : `${SELECTED_BORDER_WIDTH}px solid transparent`,
        overflow: 'hidden',
        opacity: disabled ? 0.5 : 1,
      },
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}
  >
    <CardActionArea
      className="care-program-card__action-area"
      onClick={onClick}
      disabled={disabled || !onClick}
      // Disable MUI's default interactive feedback (ripple + focus/hover overlay).
      disableRipple
      disableTouchRipple
      focusRipple={false}
      aria-label={ariaLabel || title}
      aria-pressed={selected}
      sx={{
        width: '100%',
        p: 1.5,
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        textAlign: 'left',
        '&:hover': { bgcolor: 'background.paper' },
        '&.Mui-disabled': { opacity: 1 },
      }}
    >
      <Box
        className="care-program-card__content"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          minWidth: 0,
          flex: 1,
        }}
      >
        <Box
          className="care-program-card__icon"
          sx={{
            width: ICON_CONTAINER_SIZE,
            height: ICON_CONTAINER_SIZE,
            borderRadius: '50%',
            bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.15),
            // Drives `currentColor` inside the imported SVGs (brand secondary),
            // matching the mobile icons.
            color: (theme) => theme.palette.secondary.main,
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
            '& svg': {
              width: ICON_SIZE,
              height: ICON_SIZE,
              // Imported SVGs use fill="currentColor" on their <rect>,
              // so they inherit the container color for cobranding.
              fill: 'currentColor',
            },
          }}
        >
          {icon ?? getDefaultIcon(programType)}
        </Box>

        <Typography
          className="care-program-card__title"
          variant="callout"
          sx={{
            lineHeight: 1.3125,
            color: (theme) => theme.palette.grey[900],
            overflowWrap: 'anywhere',
          }}
        >
          {title}
        </Typography>
      </Box>

      {statusLabel ? (
        <StatusPillWrapper className="care-program-card__status">
          <CareProgramStatusPill label={statusLabel} />
        </StatusPillWrapper>
      ) : null}
    </CardActionArea>
  </Card>
);

export default CareProgramCard;
